/**
 * Renders AI chat messages with basic formatting:
 * - **bold** text
 * - Bullet points (- or *)
 * - Numbered lists (1. 2. 3.)
 * - Line breaks
 * - → arrow indicators
 * - "Here's what's working:" / "Here's what needs work:" headers
 */

export default function FormattedMessage({ content }: { content: string }) {
  const lines = content.split("\n");

  const elements = lines.map((line, i) => {
    const trimmed = line.trim();

    // Empty line = paragraph break
    if (!trimmed) return <div key={i} className="h-2" />;

    // Bullet points (- or * at start)
    if (/^[-*]\s/.test(trimmed)) {
      const text = trimmed.replace(/^[-*]\s+/, "");
      return (
        <div key={i} className="flex items-start gap-2 ml-2 my-0.5">
          <span className="text-blue-500 mt-1 shrink-0">&bull;</span>
          <span>{renderInline(text)}</span>
        </div>
      );
    }

    // Arrow indicators (→ or >)
    if (/^[→>]\s/.test(trimmed)) {
      const text = trimmed.replace(/^[→>]\s+/, "");
      return (
        <div key={i} className="flex items-start gap-2 ml-3 my-0.5 pl-2 border-l-2 border-blue-300">
          <span>{renderInline(text)}</span>
        </div>
      );
    }

    // Numbered list (1. 2. 3.)
    if (/^\d+\.\s/.test(trimmed)) {
      const num = trimmed.match(/^(\d+)\./)?.[1];
      const text = trimmed.replace(/^\d+\.\s+/, "");
      return (
        <div key={i} className="flex items-start gap-2 ml-2 my-0.5">
          <span className="text-blue-600 font-semibold shrink-0 w-5 text-right">{num}.</span>
          <span>{renderInline(text)}</span>
        </div>
      );
    }

    // Headers (bold lines that end with : or are all caps-like)
    if (/^(Here's what|One question|Feedback|Score|Next question|Strengths|Weaknesses)/i.test(trimmed) || trimmed.endsWith(":")) {
      return (
        <div key={i} className="font-semibold text-slate-900 mt-2 mb-0.5">
          {renderInline(trimmed)}
        </div>
      );
    }

    // Regular paragraph
    return <div key={i} className="my-0.5">{renderInline(trimmed)}</div>;
  });

  return <div className="text-sm leading-relaxed">{elements}</div>;
}

/** Render inline formatting: **bold**, *italic* */
function renderInline(text: string): React.ReactNode {
  // Split by **bold** patterns
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return <strong key={i} className="font-semibold text-slate-900">{part.slice(2, -2)}</strong>;
    }
    // Check for *italic*
    const italicParts = part.split(/(\*[^*]+\*)/g);
    if (italicParts.length > 1) {
      return italicParts.map((ip, j) => {
        if (ip.startsWith("*") && ip.endsWith("*") && !ip.startsWith("**")) {
          return <em key={`${i}-${j}`}>{ip.slice(1, -1)}</em>;
        }
        return <span key={`${i}-${j}`}>{ip}</span>;
      });
    }
    return <span key={i}>{part}</span>;
  });
}
